import React, { useEffect, useRef } from 'react';
import { useVerificationStore } from '../../context/VerificationContext';
import { ReportMetadata } from '../../types/ViewModel/Verification/ReportMetadata';
import useGetVerificationData from '../../api/useGetVerificationData';
import { addDateDays, isDateBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { formatDateTime } from 'odfjell-date-time-utils/dateTimeFormatter';
import { EmptyButton, TabLineDDButton } from '../../styles/button.style';
import { DownloadReportWrapper } from '../../pages/verification/Verification.styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export enum tabEnum {
    details = 1,
    verification = 2,
}

const DownloadReportButtonComponent: React.FC = () => {
    const { selectedReportMetadata, selectedYears, initialSelectedYears, isDownloadDDExpanded, setIsDownloadDDExpanded } = useVerificationStore();
    const { getGeneratedDnvReport, getOvdDnvReport } = useGetVerificationData();
    const ref = useRef<HTMLInputElement>(null);

    // Close dropdown when clicking outside the wrapper
    useEffect(() => {
        const onDocMouseDown = (event: MouseEvent) => {
            if (isDownloadDDExpanded && ref.current && !ref.current.contains(event.target as Node)) {
                setIsDownloadDDExpanded(false);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [isDownloadDDExpanded, setIsDownloadDDExpanded]);

    const onToggleExpandDownloadButton = () => {
        if (isDownloadDDExpanded) {
            setIsDownloadDDExpanded(false);
        } else {
            setIsDownloadDDExpanded(true);
        }
    };

    const downloadGeneratedDnvReport = async (reportMetadata: ReportMetadata) => {
        await getGeneratedDnvReport(reportMetadata.latestReportFileName);
    };

    const downloadOvdDnvReport = async (reportMetadata: ReportMetadata) => {
        const yearsToUse = selectedYears && selectedYears.length > 0 ? selectedYears : initialSelectedYears;
        const years = (yearsToUse ?? []).map((x) => Number(x.value)).filter((y) => Number.isFinite(y));

        if (years.length === 0) {
            return;
        }

        years.sort((a, b) => a - b);
        const minDate = new Date(years[0], 0, 1);
        const now = new Date();
        let maxDate = new Date(years[years.length - 1], 11, 31);
        if (isDateBefore(now, maxDate)) {
            maxDate = new Date();
        }

        maxDate = addDateDays(maxDate, 1);
        const startDateStr = formatDateTime(minDate, 'yyyy-MM-dd');
        const endDateStr = formatDateTime(maxDate, 'yyyy-MM-dd');

        await getOvdDnvReport(reportMetadata.imoNumber, reportMetadata.reportType, startDateStr, endDateStr);
    };

    return (
        <>
            {selectedReportMetadata && (
                <DownloadReportWrapper ref={ref}>
                    <TabLineDDButton disabled={!selectedReportMetadata?.latestReportFileName}>
                        <div
                            className="dd-main"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onToggleExpandDownloadButton();
                            }}
                        >
                            <span>Download</span>
                            <FontAwesomeIcon title="Click to expand" icon={'chevron-down'} />
                        </div>

                        {isDownloadDDExpanded && (
                            <ul className="dd-list">
                                <li className="first-item">
                                    <EmptyButton type="button" onClick={() => downloadGeneratedDnvReport(selectedReportMetadata)}>
                                        Generated report
                                    </EmptyButton>
                                </li>
                                <li>
                                    <EmptyButton
                                        type="button"
                                        disabled={
                                            !selectedYears?.some((y) => Number.isFinite(Number(y.value))) &&
                                            !initialSelectedYears?.some((y) => Number.isFinite(Number(y.value)))
                                        }
                                        onClick={() => downloadOvdDnvReport(selectedReportMetadata)}
                                    >
                                        Report from DNV
                                    </EmptyButton>
                                </li>
                            </ul>
                        )}
                    </TabLineDDButton>
                </DownloadReportWrapper>
            )}
        </>
    );
};
export default DownloadReportButtonComponent;
